import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { HydratedPost, UserData } from '../types';
import {
    LikeIcon,
    CheckmarkIcon,
    BookmarkIcon,
    CommentIcon,
    BumpIcon,
    SendIcon,
    EditIcon,
    TrashIcon,
    VolumeOffIcon,
    BrokenHeartIcon,
    FlagIcon,
    MoreHorizontalIcon
} from './Icons';

interface PostActionWheelProps {
    post: HydratedPost;
    currentUser: string | null;
    usersData: Record<string, UserData>;
    onLike: (postId: number) => void;
    onBookmark: (postId: number) => void;
    onBump: (post: HydratedPost) => void;
    onShare?: (post: HydratedPost, slideIndex?: number) => void;
    onEdit: (post: HydratedPost) => void;
    onDelete: (postId: number) => void;
    onVouch?: (postId: number) => void;
    onMuteUser?: (userId: string) => void;
    onBlockUser?: (userId: string) => void;
    onReportPost?: (postId: number) => void;
    onArchive?: (postId: number) => void;
    onUnarchive?: (postId: number) => void;
    handlePostClickWithMediaStop: (e: React.MouseEvent) => void;
    animatingLikes: Set<number>;
    animatingBumps: Set<number>;
    animatingBookmarks: Set<number>;
    postBlurOpacity: number;
    postColorRgb?: string | null;
    columns: number;
    setIsWheelVisible: (visible: boolean) => void;
    handleCloseWheel: (e: React.MouseEvent | React.TouchEvent) => void;
    isSimpleBump?: boolean;
    bumperAuthor?: string;
    onPin?: (postId: number) => void;
    onUnpin?: (postId: number) => void;
    isDemo?: boolean;
    currentImageIndex?: number;
    onToggleFit?: (postId: number, slideIndex: number) => void;
    onToggleShape?: (postId: number) => void;
}

export const PostActionWheel = ({
    post,
    currentUser,
    usersData,
    onLike,
    onBookmark,
    onBump,
    onShare,
    onEdit,
    onDelete,
    onVouch,
    onMuteUser,
    onBlockUser,
    onReportPost,
    onArchive,
    onUnarchive,
    handlePostClickWithMediaStop,
    animatingLikes,
    animatingBumps,
    animatingBookmarks,
    setIsWheelVisible,
    handleCloseWheel,
    columns,
    isDemo = false,
    isSimpleBump = false,
    bumperAuthor,
    onPin,
    onUnpin,
    currentImageIndex = 0,
    onToggleFit,
    onToggleShape
}: PostActionWheelProps) => {
    const [canClose, setCanClose] = React.useState(false);
    const [isVisible, setIsVisible] = React.useState(false);
    const [isOptionsOpen, setIsOptionsOpen] = React.useState(false);
    const sheetRef = useRef<HTMLDivElement>(null);
    const optionsMenuRef = useRef<HTMLDivElement>(null);

    // Animate in — double rAF ensures the initial hidden state is painted first
    useEffect(() => {
        const raf1 = requestAnimationFrame(() => {
            requestAnimationFrame(() => setIsVisible(true));
        });
        const closeTimer = setTimeout(() => setCanClose(true), 250);
        return () => {
            cancelAnimationFrame(raf1);
            clearTimeout(closeTimer);
        };
    }, []);

    // Close options menu and dismiss the entire action bar (wheel) when clicking outside the options menu
    useEffect(() => {
        if (!isOptionsOpen) return;
        const handleOutsideClick = (e: MouseEvent | TouchEvent) => {
            if (optionsMenuRef.current && !optionsMenuRef.current.contains(e.target as Node)) {
                setIsOptionsOpen(false);
                dismiss();
            }
        };
        window.addEventListener('mousedown', handleOutsideClick, true);
        window.addEventListener('touchstart', handleOutsideClick, true);
        return () => {
            window.removeEventListener('mousedown', handleOutsideClick, true);
            window.removeEventListener('touchstart', handleOutsideClick, true);
        };
    }, [isOptionsOpen]);

    const dismiss = () => {
        setIsVisible(false);
        setTimeout(() => setIsWheelVisible(false), 220);
    };

    const handleActionClick = (action: () => void) => {
        action();
        dismiss();
    };

    const handleOverlayTouch = (e: React.TouchEvent | React.MouseEvent) => {
        if (!canClose) return;
        if (sheetRef.current?.contains(e.target as Node)) return;
        setIsVisible(false);
        setTimeout(() => handleCloseWheel(e as any), 220);
    };

    const isOwner = (post.author === currentUser && !isSimpleBump) ||
        (isSimpleBump && bumperAuthor === currentUser);

    const hasRefittableMedia = post.type === 'image' || post.type === 'video' || ((post.type === 'audio' || post.type === 'voice') && !!post.content.coverImageUrl);

    // Icons sized for touch — fits comfortably across 80% width
    const iconSm = 'w-6 h-6';
    const btnCls = 'flex-1 flex items-center justify-center p-2 rounded-full transition-all duration-150 active:scale-95 hover:bg-white/10 min-w-0';

    // 1-column: overlay is absolute (covers the postcard itself, no portal needed)
    // 2-3 column: portal to body so it centers on the full screen
    const positionCls = columns === 1
        ? 'absolute inset-0 z-[9999] flex items-center justify-center'
        : 'fixed inset-0 z-[9999] flex items-center justify-center';

    const overlay = (
        <div
            className={positionCls}
            style={{
                backgroundColor: isVisible ? 'rgba(0,0,0,0.55)' : 'rgba(0,0,0,0)',
                transition: 'background-color 0.22s ease',
                backdropFilter: isVisible ? 'blur(3px)' : 'none',
                WebkitBackdropFilter: isVisible ? 'blur(3px)' : 'none',
            }}
            onClick={handleOverlayTouch}
        >
            {/* Centered pill + dropdown */}
            <div
                ref={sheetRef}
                className={`flex flex-col items-center gap-2 ${isDemo ? 'opacity-60' : ''}`}
                style={{
                    opacity: isVisible ? 1 : 0,
                    transform: isVisible ? 'scale(1) translateY(0)' : 'scale(0.92) translateY(12px)',
                    transition: 'opacity 0.22s ease, transform 0.22s cubic-bezier(0.34,1.56,0.64,1)',
                }}
                onClick={e => e.stopPropagation()}
                onTouchEnd={e => e.stopPropagation()}
            >
                {/* More dropdown — opens above the pill */}
                {isOptionsOpen && (
                    <div
                        ref={optionsMenuRef}
                        className="w-48 bg-gray-900/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-700/50 py-1.5 overflow-hidden"
                    >
                        {post.author !== currentUser && (
                            <>
                                <button
                                    onClick={() => handleActionClick(() => onReportPost?.(post.id))}
                                    className="w-full text-left px-4 py-2.5 hover:bg-yellow-500/10 text-yellow-500 transition-colors flex items-center gap-3"
                                >
                                    <FlagIcon className="w-4 h-4 text-yellow-500" />
                                    <span className="text-sm font-medium">Report Content</span>
                                </button>
                                <button
                                    onClick={() => handleActionClick(() => onMuteUser?.(post.author))}
                                    className="w-full text-left px-4 py-2.5 hover:bg-white/10 transition-colors flex items-center gap-3"
                                >
                                    <VolumeOffIcon className="w-4 h-4 text-gray-400" />
                                    <span className="text-sm font-medium text-white">
                                        {usersData[currentUser || '']?.mutedUsers?.includes(post.author) ? 'Unmute' : 'Mute'} @{post.author}
                                    </span>
                                </button>
                                <button
                                    onClick={() => handleActionClick(() => onBlockUser?.(post.author))}
                                    className="w-full text-left px-4 py-2.5 hover:bg-red-500/10 text-red-400 transition-colors flex items-center gap-3"
                                >
                                    <BrokenHeartIcon className="w-4 h-4 text-red-500" />
                                    <span className="text-sm font-medium">
                                        {usersData[currentUser || '']?.blockedUsers?.includes(post.author) ? 'Unblock' : 'Block'} @{post.author}
                                    </span>
                                </button>
                            </>
                        )}
                        {post.author === currentUser && (
                            <div className="px-4 py-2 text-xs text-gray-500 font-semibold uppercase tracking-wider">Post Options</div>
                        )}

                        {post.author === currentUser && (
                            <>
                                <button
                                    onClick={() => handleActionClick(() => onBookmark(post.id))}
                                    className="w-full text-left px-4 py-2.5 hover:bg-white/10 text-white transition-colors flex items-center gap-3 border-t border-gray-700/50"
                                >
                                    <BookmarkIcon isBookmarked={post.isBookmarked} className="w-4 h-4 text-white" />
                                    <span className="text-sm font-medium">{post.isBookmarked ? 'Unbookmark Post' : 'Bookmark Post'}</span>
                                </button>
                                <button
                                    onClick={() => handleActionClick(() => {
                                        const isPinned = usersData[currentUser || '']?.pinnedPosts?.includes(post.id);
                                        if (isPinned) onUnpin?.(post.id);
                                        else onPin?.(post.id);
                                    })}
                                    className="w-full text-left px-4 py-2.5 hover:bg-blue-500/10 text-blue-400 transition-colors flex items-center gap-3 border-t border-gray-700/50"
                                >
                                    <span className="text-sm font-medium">
                                        {usersData[currentUser || '']?.pinnedPosts?.includes(post.id) ? '📌 Unpin Post' : '📍 Pin Post'}
                                    </span>
                                </button>
                                <button
                                    onClick={() => handleActionClick(() => {
                                        if (post.isArchived) onUnarchive?.(post.id);
                                        else onArchive?.(post.id);
                                    })}
                                    className="w-full text-left px-4 py-2.5 hover:bg-yellow-500/10 text-yellow-400 transition-colors flex items-center gap-3 border-t border-gray-700/50"
                                >
                                    <BookmarkIcon className={`w-4 h-4 ${post.isArchived ? 'text-blue-400' : 'text-yellow-500'}`} />
                                    <span className="text-sm font-medium">
                                        {post.isArchived ? 'Unarchive Post' : 'Archive Post'}
                                    </span>
                                </button>
                                <button
                                    onClick={() => handleActionClick(() => onDelete(post.id))}
                                    className="w-full text-left px-4 py-2.5 hover:bg-red-500/10 text-red-400 transition-colors flex items-center gap-3 border-t border-gray-700/50"
                                >
                                    <TrashIcon className="w-4 h-4 text-red-500" />
                                    <span className="text-sm font-medium">
                                        {isSimpleBump ? 'Remove Bump' : 'Delete Post'}
                                    </span>
                                </button>
                            </>
                        )}
                    </div>
                )}

                {/* Main pill — buttons split width evenly, reversed order, More stays last */}
                <div className="flex items-center text-white bg-black/50 backdrop-blur-sm rounded-full p-1 shadow-lg overflow-hidden" style={{ width: columns === 1 ? '120%' : '80vw' }}>

                    {/* Like / Vouch */}
                    {post.footprintDetails ? (
                        <button
                            className={btnCls}
                            onClick={(e) => { e.stopPropagation(); handleActionClick(() => onVouch?.(post.id)); }}
                            aria-label="Vouch"
                        >
                            <CheckmarkIcon
                                isVouched={post.vouches?.some(v => v.userId === currentUser)}
                                className={iconSm}
                            />
                        </button>
                    ) : (
                        <button
                            className={btnCls}
                            onClick={(e) => { e.stopPropagation(); handleActionClick(() => onLike(post.id)); }}
                            aria-label="Like"
                        >
                            <LikeIcon
                                isLiked={post.isLiked}
                                className={`${iconSm} ${animatingLikes.has(post.id) ? 'animate-heart-burst' : ''}`}
                            />
                        </button>
                    )}

                    {/* Comment */}
                    <button
                        className={btnCls}
                        onClick={(e) => { e.stopPropagation(); handleActionClick(() => handlePostClickWithMediaStop(e as any)); }}
                        aria-label="Comment"
                    >
                        <CommentIcon className={iconSm} />
                    </button>

                    {/* Share */}
                    <button
                        className={btnCls}
                        onClick={(e) => { e.stopPropagation(); handleActionClick(() => onShare?.(post)); }}
                        aria-label="Share"
                    >
                        <SendIcon className={iconSm} />
                    </button>



                    {/* Bump */}
                    <button
                        className={btnCls}
                        onClick={(e) => { e.stopPropagation(); handleActionClick(() => onBump(post)); }}
                        aria-label="Bump"
                    >
                        <BumpIcon
                            isBumped={post.isBumped}
                            className={`${iconSm} ${animatingBumps.has(post.id) ? 'animate-bump-spin' : ''}`}
                        />
                    </button>

                    {/* Shape Toggle */}
                    {post.author === currentUser && post.type !== 'link' && post.type !== 'text' && (
                        <button
                            className={btnCls}
                            onClick={(e) => {
                                e.stopPropagation();
                                onToggleShape?.(post.id);
                            }}
                            aria-label="Toggle Shape"
                            title="Toggle Shape"
                        >
                            <span className="text-[10px] font-black uppercase tracking-tighter leading-none px-1">
                                {post.postcardShape === 'square' ? '1:1' : (post.postcardShape === 'tall' ? 'tall' : 'wide')}
                            </span>
                        </button>
                    )}

                    {/* Bookmark / Refit Toggle */}
                    {post.author === currentUser ? (
                        hasRefittableMedia && (
                            <button
                                className={btnCls}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onToggleFit?.(post.id, currentImageIndex);
                                }}
                                aria-label="Refit Media"
                                title="Refit Media"
                            >
                                {(() => {
                                    const isAudioVoice = post.type === 'audio' || post.type === 'voice';
                                    const currentFit = isAudioVoice ? (post.content.coverImageFit || 'auto') : (post.content.media?.[currentImageIndex]?.fit || 'auto');
                                    if (currentFit === 'horizontal') {
                                        return (
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={iconSm}>
                                                <path d="M3 12h18M7 8l-4 4 4 4M17 8l4 4-4 4" />
                                            </svg>
                                        );
                                    } else if (currentFit === 'vertical') {
                                        return (
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={iconSm}>
                                                <path d="M12 3v18M8 7l4-4 4 4M8 17l4 4 4-4" />
                                            </svg>
                                        );
                                    } else {
                                        return (
                                            <span className="text-[10px] font-black uppercase tracking-tighter leading-none">auto</span>
                                        );
                                    }
                                })()}
                            </button>
                        )
                    ) : (
                        <button
                            className={btnCls}
                            onClick={(e) => { e.stopPropagation(); handleActionClick(() => onBookmark(post.id)); }}
                            aria-label="Bookmark"
                        >
                            <BookmarkIcon
                                isBookmarked={post.isBookmarked}
                                className={`${iconSm} ${animatingBookmarks.has(post.id) ? 'animate-bookmark-pop' : ''}`}
                            />
                        </button>
                    )}

                    {/* Owner: Edit */}
                    {isOwner && !isSimpleBump && (
                        <button
                            className={btnCls}
                            onClick={(e) => { e.stopPropagation(); handleActionClick(() => onEdit(post)); }}
                            aria-label="Edit Post"
                        >
                            <EditIcon className={iconSm} />
                        </button>
                    )}

                    {/* More — always last */}
                    <button
                        className={`${btnCls} ${isOptionsOpen ? 'bg-white/10' : ''}`}
                        onClick={(e) => { e.stopPropagation(); setIsOptionsOpen(o => !o); }}
                        aria-label="More Options"
                    >
                        <MoreHorizontalIcon className={iconSm} />
                    </button>
                </div>
            </div>
        </div>
    );

    // 1-column: render inline (absolute, anchored to the postcard)
    // 2-3 column: portal to body (fixed, true screen-center)
    if (columns === 1) return overlay;
    return ReactDOM.createPortal(overlay, document.body);
};
